/**
 * Os textos da tela do caixa que a origem escreve e o contrato não tem.
 *
 * Papel: aqui ficam os nomes dos meses, as opções de tipo do modal e do filtro,
 * a ordem das formas no filtro "Pagamento", o tipo e a situação como a
 * planilha os escreve e o nome de cada linha de ajuda de custo do perfil.
 *
 * Como usar: o texto de cada forma de pagamento é o `ROTULOS_FORMA` de
 * `@concierge/shared`, que o modal, a tabela e a planilha dividem; o que mora
 * aqui é o que só a tela usa. As setas e os emojis dos rótulos são texto da
 * origem e ficam como estão — tirar o "⬇" de "⬇ Crédito Mov." muda o que a
 * pessoa lê no filtro.
 */
import {
  type FormaPagamentoLancamento,
  MESES_POR_EXTENSO,
  SUBCATEGORIA_DO_TIPO,
  type StatusLancamento,
  type TipoItemAjudaCusto,
  type TipoLancamento,
} from '@concierge/shared';

/** Os meses por extenso, como o `MESES` da origem (l. 1377). */
export const NOMES_DOS_MESES = MESES_POR_EXTENSO;

/** Uma opção de `select` com o valor do contrato e o texto da origem. */
export interface OpcaoDoTipo {
  valor: TipoLancamento;
  rotulo: string;
}

/** O "Tipo" do modal de lançamento, na ordem da origem (l. 774–779). */
export const OPCOES_DE_TIPO_DO_MODAL: readonly OpcaoDoTipo[] = [
  { valor: 'despesa', rotulo: '↗ Despesa' },
  { valor: 'receita', rotulo: '↙ Entradas' },
  { valor: 'credito_movimentacao', rotulo: '⬇ Crédito de Movimentação Bancária' },
  { valor: 'debito_movimentacao', rotulo: '⬆ Débito de Movimentação Bancária' },
];

/** O filtro "Tipo" de Lançamentos, depois de "Todos", na ordem da origem (l. 382–386). */
export const OPCOES_DE_TIPO_DO_FILTRO: readonly OpcaoDoTipo[] = [
  { valor: 'receita', rotulo: 'Entradas' },
  { valor: 'despesa', rotulo: 'Despesa' },
  { valor: 'credito_movimentacao', rotulo: '⬇ Crédito Mov.' },
  { valor: 'debito_movimentacao', rotulo: '⬆ Débito Mov.' },
];

/**
 * O filtro "Pagamento" de Lançamentos, depois de "Todos" (l. 401–408).
 *
 * É outra ordem que a do modal (`FORMA_PAGAMENTO_LANCAMENTO`), e a origem tem as
 * duas.
 */
export const FORMAS_DO_FILTRO: readonly FormaPagamentoLancamento[] = [
  'pix',
  'cartao_de_credito',
  'boleto',
  'transferencia',
  'debito_automatico',
  'cartao_de_debito',
  'dinheiro',
];

/** A coluna "Tipo" da planilha de lançamentos (l. 2823). */
export const TIPO_NA_PLANILHA: Readonly<Record<TipoLancamento, string>> = {
  despesa: 'Despesa',
  receita: 'Receita',
  credito_movimentacao: 'Crédito Mov.',
  debito_movimentacao: 'Débito Mov.',
};

/** A coluna "Status" da planilha de lançamentos (l. 2815). */
export const STATUS_NA_PLANILHA: Readonly<Record<StatusLancamento, string>> = {
  pago: 'Pago',
  provisionado: 'Provisionado',
  lancado: 'Lançado',
};

/** O nome de cada linha de "Ajudas de custo habilitadas" do perfil (l. 1209–1236). */
export const ROTULOS_DA_AJUDA_DE_CUSTO: Readonly<Record<TipoItemAjudaCusto, string>> =
  SUBCATEGORIA_DO_TIPO;
